import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban, CheckCircle2, RefreshCw, Search, Trash2 } from "lucide-react";
import AppLayout from "../../shared/components/AppLayout";
import Button from "../../shared/components/Button";
import adminDoctorsService, { AdminDoctor } from "../../services/adminDoctorsService";

const DoctorManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const [doctors, setDoctors] = useState<AdminDoctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);


  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDoctors(await adminDoctorsService.listDoctors());
    } catch (e: any) {
      setError(
        e?.response?.data?.error ||
          (e instanceof Error ? e.message : "Failed to load doctors."),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return doctors;
    return doctors.filter((d) =>
      [d.full_name, d.email, d.specialization ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [doctors, search]);

  const activeCount = doctors.filter((d) => d.is_active).length;

  const toggleActive = async (doc: AdminDoctor) => {
    const next = !doc.is_active;
    if (
      !next &&
      !window.confirm(
        `Disable ${doc.full_name}?\n\nThey will be signed out and blocked from logging in. All their encounters and notes are kept, and you can re-enable the account at any time.`,
      )
    ) {
      return;
    }
    setBusyId(doc.id);
    setError(null);
    setNotice(null);
    try {
      await adminDoctorsService.setActive(doc.id, next);
      setDoctors((prev) =>
        prev.map((d) =>
          d.id === doc.id
            ? { ...d, is_active: next, cognito_enabled: next }
            : d,
        ),
      );
      setNotice(`${doc.full_name} ${next ? "enabled" : "disabled"}.`);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Could not update the account.");
    } finally {
      setBusyId(null);
    }
  };

  const removeDoctor = async (doc: AdminDoctor) => {
    // Deletion is irreversible, so require the email to be typed back.
    const typed = window.prompt(
      `PERMANENTLY DELETE the login for ${doc.full_name}?\n\nThis removes their account from Cognito and the app. Existing encounters keep their authorship, but the account cannot be restored.\n\nDisabling is usually the safer choice.\n\nType the doctor's email to confirm:`,
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== doc.email.toLowerCase()) {
      setError("Email did not match — deletion cancelled.");
      return;
    }
    setBusyId(doc.id);
    setError(null);
    setNotice(null);
    try {
      await adminDoctorsService.deleteDoctor(doc.id);
      setDoctors((prev) => prev.filter((d) => d.id !== doc.id));
      setNotice(`Account deleted for ${doc.email}.`);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Could not delete the account.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AppLayout active="admin-doctors">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-start justify-between mb-6 gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Doctors</h1>
                <p className="text-gray-500 mt-1 text-sm">
                  {doctors.length} account{doctors.length !== 1 ? "s" : ""} ·{" "}
                  {activeCount} active. Disable to block sign-in without losing
                  records.
                </p>
              </div>
              <Button
                variant="secondary"
                icon={RefreshCw}
                onClick={load}
                loading={loading}
              >
                Refresh
              </Button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
            {notice && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {notice}
              </div>
            )}

            <div className="mb-4 relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email or specialty"
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-16 text-center text-sm text-gray-500">
                  {doctors.length === 0
                    ? "No doctor accounts yet."
                    : "No doctors match that search."}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <th className="px-5 py-3">Doctor</th>
                        <th className="px-5 py-3">Specialty</th>
                        <th className="px-5 py-3">Status</th>
                        {/* "Last seen" rather than "Last login": the timestamp is
                            refreshed from authenticated API activity, since
                            sign-in happens in Cognito and never touches Django. */}
                        <th className="px-5 py-3">Last seen</th>
                        <th className="px-5 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filtered.map((doc) => (
                        <tr
                          key={doc.id}
                          className={doc.is_active ? "" : "bg-gray-50/70"}
                        >
                          <td className="px-5 py-4">
                            <div className="font-medium text-gray-900">
                              {doc.full_name}
                              {doc.is_staff && (
                                <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700 align-middle">
                                  ADMIN
                                </span>
                              )}
                            </div>
                            <div className="text-gray-500 text-xs">{doc.email}</div>
                          </td>
                          <td className="px-5 py-4 text-gray-600">
                            {doc.specialization || (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                                doc.is_active
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-200 text-gray-600"
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  doc.is_active ? "bg-green-500" : "bg-gray-400"
                                }`}
                              />
                              {doc.is_active ? "Active" : "Disabled"}
                            </span>
                            {/* Surface drift between the app and Cognito rather
                                than hiding it behind the local flag. */}
                            {doc.cognito_enabled !== null &&
                              doc.cognito_enabled !== doc.is_active && (
                                <div className="text-[10px] text-amber-600 mt-1">
                                  Cognito:{" "}
                                  {doc.cognito_enabled ? "enabled" : "disabled"}
                                </div>
                              )}
                          </td>
                          <td className="px-5 py-4 text-gray-500 text-xs">
                            {/* Accounts active before last-seen tracking existed
                                have no timestamp yet, so show a neutral dash
                                rather than claiming they never signed in. */}
                            {doc.last_login ? (
                              new Date(doc.last_login).toLocaleString(undefined, {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant={doc.is_active ? "secondary" : "success"}
                                size="sm"
                                icon={doc.is_active ? Ban : CheckCircle2}
                                loading={busyId === doc.id}
                                onClick={() => toggleActive(doc)}
                              >
                                {doc.is_active ? "Disable" : "Enable"}
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                icon={Trash2}
                                disabled={busyId === doc.id}
                                onClick={() => removeDoctor(doc)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
    </AppLayout>
  );
};

export default DoctorManagementPage;
